import { DocumentNode, gql } from '@apollo/client';
import { Stack, Text } from '@chakra-ui/react';
import { dinero, isZero } from 'dinero.js';
import * as React from 'react';

import {
  Abbreviate,
  AbbreviateContent,
  AbbreviateToggle,
} from '@/components/Abbreviate';
import { BigRadio, BigRadioGroup } from '@/components/BigRadio';
import { CurrencyDisplay } from '@/components/Currency';
import { ETicketCanPurchaseType } from '@/graphql-types/globalTypes';
import { StarIcon } from '@/imports/ui/chakra/feather';

import { TicketSelect_EventFragment } from './graphql-types/TicketSelect_EventFragment';

export const TicketSelect: React.FC<{
  value?: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
  event: TicketSelect_EventFragment;
}> & { eventFragment: DocumentNode } = (props) => {
  return (
    <BigRadioGroup
      value={props.value}
      onChange={props.onChange}
      variant="spaced"
      isDisabled={props.isDisabled}
    >
      {props.event.ticketsCurrentUserCanPurchase.map((ticket) => {
        const price = dinero(ticket.price);
        const isFree = isZero(price);

        return (
          <BigRadio key={ticket._id} value={ticket._id}>
            <Stack direction="row" alignItems="center">
              <Text textStyle="xlBodyStrong">{ticket.name}</Text>
              {ticket.canPurchase.type ===
                ETicketCanPurchaseType.MAGIC_LINK && (
                <StarIcon boxSize="3" color="primary.500" fill="currentcolor" />
              )}
            </Stack>

            {isFree ? (
              <Text>Free</Text>
            ) : (
              <CurrencyDisplay value={dinero(ticket.price)} />
            )}

            {ticket.description && (
              <Abbreviate spacing="1" noOfLines={2}>
                <AbbreviateContent
                  as={Text}
                  textStyle="compact"
                  color="gray.600"
                >
                  {ticket.description}
                </AbbreviateContent>

                <span
                  onClick={(event) => {
                    // clicking on the toggle shouldn't trigger clicking on the radio
                    event.preventDefault();
                  }}
                >
                  <AbbreviateToggle size="sm" />
                </span>
              </Abbreviate>
            )}
          </BigRadio>
        );
      })}
    </BigRadioGroup>
  );
};

TicketSelect.eventFragment = gql`
  fragment TicketSelect_EventFragment on EventV2 {
    _id
    ticketsCurrentUserCanPurchase {
      _id
      name
      description
      price
      canPurchase {
        ... on Ticket_CanPurchase_MagicLink {
          type
        }
        ... on Ticket_CanPurchase_PermissionConfig {
          type
        }
      }
    }
  }
`;
